import {
  dynamicInputConfigEntrySchema,
  type DynamicInputConfigEntry,
} from "@erp/shared-types";
import type { SocketType } from "../../types";
import {
  buildDynamicSockets,
  effectiveSocketIdVersion,
} from "./dynamicSocketId";
import type { DynamicSocketDefinition } from "./socketDefinition";
import { prettify } from "./socketDefinition";

/**
 * Generate input sockets for csv_transform_v4. Always includes the static
 * `inputDocument` socket (the document to transform), followed by any
 * user-defined extra sockets from `dynamicInputs` config. Extra sockets become
 * wirable string values on the canvas; they are available in formula columns as
 * $$name and injected as context for AI columns.
 */
export function generateCsvTransformV4InputSockets(
  config: Record<string, unknown>,
): DynamicSocketDefinition[] {
  const staticSockets: DynamicSocketDefinition[] = [
    {
      id: "inputDocument",
      name: "Input Document",
      type: "entity_reference" as SocketType,
      required: true,
      description: "The document to transform",
    },
  ];

  const rawDynamicInputs = config.dynamicInputs;
  const dynamicInputsArray: unknown[] = Array.isArray(rawDynamicInputs)
    ? rawDynamicInputs
    : [];

  const dynamicInputs = dynamicInputsArray
    .map((d) => dynamicInputConfigEntrySchema.safeParse(d).data)
    .filter((d): d is DynamicInputConfigEntry => d !== undefined);

  const { sockets: extraSockets } = buildDynamicSockets({
    reserved: staticSockets.map((s) => s.id),
    version: effectiveSocketIdVersion("csv_transform_v4", config),
    entries: dynamicInputs.map((d) => ({
      name: d.name,
      socket: {
        name:
          typeof d.label === "string" && d.label.length > 0
            ? d.label
            : prettify(d.name),
        type: "string" as SocketType,
        required: false,
        description:
          typeof d.description === "string" && d.description.length > 0
            ? d.description
            : `Available in formulas as $$${d.name}`,
      },
    })),
  });

  return [...staticSockets, ...extraSockets];
}
